import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { SEARCH_EXPERIENCES_MODAL, SEARCH_EXPERIENCES_MODAL_TAB_INDEX } from '@/utils/state';
import type { MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import { sendGAWabunkaSectionViewAllEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionViewAll';
import { SEARCH_TAB_INDEX } from '@/wabunka-src/constants/navigation';
import { useTopThemeActions } from '@/wabunka-src/features/Top/composables/general/useTopThemeActions';

/**
 * トップページ（control / variant 共通）の GA 計測・ナビゲーション・モーダル操作ハンドラを集約する composable。
 *
 * ページコンポーネント（`pages/index.vue`）を薄く保つため、UI イベントのコールバック群をここへ切り出す
 * （アーキ規約「Component の責務は UI 表示・状態・composable 呼び出しのみ」に沿う）。Theme セクションの
 * 操作は {@link useTopThemeActions} を内包して窓口を一本化する。
 */
export const useTopPageHandlers = () => {
  const route = useRoute();
  const { open: openSearchExperiencesModal } = useGlobalModal(SEARCH_EXPERIENCES_MODAL);
  const { updateTabIndex: updateSearchExperiencesModalTabIndex } = useCurrentTabIndex(SEARCH_EXPERIENCES_MODAL_TAB_INDEX);
  const { onClickThemeViewAll } = useTopThemeActions();

  /** PickUp セクションへスムーススクロール（KV のボタンと body の両方から使う） */
  const scrollToPickUp = () => {
    const pickUp = document.getElementById('pick-up');
    pickUp?.scrollIntoView({ behavior: 'smooth' });
  };

  /** コンタクトボタン */
  const onClickContact = () => {
    navigateTo('/contact/');
  };

  /** パートナーボタン */
  const onClickPartner = () => {
    navigateTo('/lp/partner-with-us/');
  };

  /** Search Experiences ボタン */
  const onClickExperiencesButton = () => {
    navigateTo('/experiences/');
  };

  /** Story セクションの「View all stories」（GA 送信のみ。遷移は StorySection 側の NuxtLink が担う） */
  const onClickStoryViewAll = () => {
    sendGAWabunkaSectionViewAllEvent('stories', route.name as string);
  };

  /** 体験カードクリック */
  const onExperienceClick = (sectionId: string, experienceSlug: string) => {
    sendGAWabunkaSectionSlugViewEvent(sectionId, experienceSlug, route.name as string);
  };

  /** もっと見るクリック */
  const onViewAll = (sectionId: string) => {
    sendGAWabunkaSectionViewAllEvent(sectionId, route.name as string);
  };

  /** フィーチャードカードクリック */
  const onFeaturedClick = (type: string, id: string) => {
    sendGAWabunkaSectionSlugViewEvent(type, id, route.name as string);
  };

  /** Destinations カードクリック。section_id は旧 FeaturedSection の area と揃える */
  const onDestinationClick = (destination: MinimumFeaturedViewModel) => {
    sendGAWabunkaSectionSlugViewEvent('featured-area', destination.id, route.name as string);
  };

  /** 「View all destinations」クリックで検索モーダルを Destinations タブで開く */
  const onClickDestinationsViewAll = () => {
    sendGAWabunkaSectionViewAllEvent('featured-area', route.name as string);
    updateSearchExperiencesModalTabIndex(SEARCH_TAB_INDEX.DESTINATIONS);
    openSearchExperiencesModal();
  };

  return {
    scrollToPickUp,
    onClickContact,
    onClickPartner,
    onClickExperiencesButton,
    onClickStoryViewAll,
    onExperienceClick,
    onViewAll,
    onFeaturedClick,
    onDestinationClick,
    onClickDestinationsViewAll,
    onClickThemeViewAll,
  };
};
